import { useState } from "react";
import { Ruler, Megaphone, Camera, PenLine } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";

const ARGUMENTS = [
  { icon: Ruler, title: "Estimation précise", text: "Une valeur juste, appuyée sur les transactions réelles de votre secteur." },
  { icon: Megaphone, title: "Stratégie sur mesure", text: "Un plan de commercialisation adapté à votre bien et à vos délais." },
  { icon: Camera, title: "Mise en valeur pro", text: "Photographie soignée, visite immersive et présentation haut de gamme." },
  { icon: PenLine, title: "Jusqu'à la signature", text: "Visites, négociation et coordination notariale prises en charge." },
];

const fieldCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-light text-azur outline-none transition-colors focus:border-azur";

export default function Vendre() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", type_bien: "", localisation: "", surface: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", {
        kind: "estimation",
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        details: { type_bien: form.type_bien, localisation: form.localisation, surface: form.surface },
      });
      toast.success("Votre demande d'estimation a bien été envoyée. Nous vous recontactons sous 24 h.");
      setForm({ name: "", email: "", phone: "", type_bien: "", localisation: "", surface: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Vendre"
        title="Vous souhaitez vendre ? Confiez-nous votre projet."
        sub="Votre bien mérite une présentation à sa hauteur — et une stratégie de vente à la hauteur de vos attentes."
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16 lg:py-24">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ARGUMENTS.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08}>
              <div className="group h-full rounded-2xl border border-slate-200/80 bg-white p-7 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_12px_40px_rgba(12,35,64,0.08)]">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-azur text-sand transition-colors duration-500 group-hover:bg-terracotta">
                  <a.icon size={19} />
                </span>
                <h3 className="mt-5 font-serif text-xl font-semibold text-azur">{a.title}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-slate-600">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid gap-12 lg:grid-cols-5 lg:gap-16">
          <Reveal className="lg:col-span-2">
            <span className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-terracotta">
              Estimation gratuite
            </span>
            <h2 className="mt-4 font-serif text-2xl font-normal tracking-tight text-azur sm:text-3xl lg:text-4xl">
              Combien vaut votre bien aujourd'hui ?
            </h2>
            <p className="mt-5 font-light leading-relaxed text-slate-600">
              Décrivez-nous votre propriété : nous vous transmettons une première fourchette de valeur sous 24 h,
              puis affinons ensemble lors d'une visite gratuite et sans engagement.
            </p>
            <p className="mt-6 font-serif text-lg italic text-azur/70">
              « Un prix juste, défendable, qui vend. »
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form
              onSubmit={submit}
              data-testid="estimation-form"
              className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)]"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <input required placeholder="Nom et prénom" value={form.name} onChange={set("name")} data-testid="estimation-name" className={fieldCls} />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="estimation-email" className={fieldCls} />
                <input placeholder="Téléphone" value={form.phone} onChange={set("phone")} data-testid="estimation-phone" className={fieldCls} />
                <select required value={form.type_bien} onChange={set("type_bien")} data-testid="estimation-type" className={fieldCls}>
                  <option value="">Type de bien</option>
                  <option value="Villa">Villa</option>
                  <option value="Appartement">Appartement</option>
                  <option value="Maison">Maison</option>
                  <option value="Terrain">Terrain</option>
                  <option value="Autre">Autre</option>
                </select>
                <input required placeholder="Localisation (ville, quartier)" value={form.localisation} onChange={set("localisation")} data-testid="estimation-location" className={fieldCls} />
                <input placeholder="Surface approximative" value={form.surface} onChange={set("surface")} data-testid="estimation-surface" className={fieldCls} />
              </div>
              <textarea
                rows={4}
                placeholder="Parlez-nous de votre bien (vue, terrasse, état, particularités…)"
                value={form.message}
                onChange={set("message")}
                data-testid="estimation-message"
                className={fieldCls + " mt-4 resize-none"}
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="estimation-submit"
                className="mt-6 w-full rounded-xl bg-azur px-6 py-4 text-sm font-medium text-sand transition-colors duration-300 hover:bg-terracotta disabled:opacity-60"
              >
                {sending ? "Envoi en cours…" : "Demander mon estimation gratuite"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
